import React, { memo, useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import NotificationIcon from '@atlaskit/icon/glyph/notification';

import { type Notification } from '@atlassian/notification-types/types';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { type FlagsStateContainer, withFlags } from '@confluence/flags';

import { isExperimentEnabled } from './services/experimentation';
import {
	getFlyoutMessageDisabledValue,
	getFlyoutDateLastShownValue,
	setFlyoutDateLastShown,
} from './localStorage';
import { NotificationDescription } from './NotificationDescription';
import { Title } from './Title';
import { useExperimentEligibility } from './useExperimentEligibility';
// TODO remove this when we have a real notification
import notificationData from './notification.json';
import { i18n } from './translations';
import { useCallbackFlyoutAnalytics, useSendFlyoutAnalytics } from './analytics';

interface FlyoutMessagingComponentProps {
	onOpenNotifDrawer: (e: any) => void;
	flags: FlagsStateContainer;
}

export const MS_BEFORE_SHOWING_FLYOUT = 60000; // 1 minutes in milliseconds

// TODO remove this when we have a real notification
const sampleNotification: Notification = notificationData as Notification;

const FlyoutMessagingComponentWrapper = ({
	onOpenNotifDrawer,
	flags,
}: FlyoutMessagingComponentProps) => {
	const { formatMessage } = useIntl();
	const hideFlag = useCallback(() => {
		void flags.hideFlag('flyout-messaging-experiment');
	}, [flags]);

	const isEligibleForExperiment = useExperimentEligibility();
	const wasShownToday = () => {
		const lastShown = getFlyoutDateLastShownValue();
		const today = new Date().toLocaleDateString();
		return lastShown === today;
	};

	const shouldShowFlag = useCallback(() => {
		return (
			isEligibleForExperiment &&
			isExperimentEnabled() &&
			!getFlyoutMessageDisabledValue() &&
			!wasShownToday()
		);
	}, [isEligibleForExperiment]);

	const onOpenNotifDrawerWithAnalytics = useCallbackFlyoutAnalytics(onOpenNotifDrawer, {
		actionSubjectId: 'openNotificationsDrawerCta',
		notification: sampleNotification,
	});

	const sendDismissAnalytics = useSendFlyoutAnalytics({
		action: 'dismissed',
		actionSubjectId: 'flyout',
		notification: sampleNotification,
	});

	const showFlag = useCallback(() => {
		void flags.showCustomFlag({
			id: 'flyout-messaging-experiment',
			isAutoDismiss: true,
			onClose: sendDismissAnalytics,
			title: <Title unreadCount={25} notification={sampleNotification} />,
			description: (
				<NotificationDescription notification={sampleNotification} hideFlag={hideFlag} />
			),
			customIcon: <NotificationIcon label="Notification icon" />,
			actions: [
				{
					content: formatMessage(i18n.openNotifDrawerAction),
					onClick: onOpenNotifDrawerWithAnalytics,
				},
			],
		});
		setFlyoutDateLastShown();
	}, [flags, formatMessage, onOpenNotifDrawerWithAnalytics, hideFlag, sendDismissAnalytics]);

	const showFlagWithAnalytics = useCallbackFlyoutAnalytics(showFlag, {
		action: 'shown',
		type: 'sendTrackEvent',
		actionSubjectId: 'flyout',
		notification: sampleNotification,
	});

	useEffect(() => {
		if (shouldShowFlag()) {
			const timer = setTimeout(() => {
				showFlagWithAnalytics();
			}, MS_BEFORE_SHOWING_FLYOUT);

			return () => clearTimeout(timer);
		}
	}, [shouldShowFlag, showFlagWithAnalytics]);

	return null;
};

export const FlyoutMessagingComponent = withTransparentErrorBoundary({
	attribution: Attribution.NOTIFICATIONS,
})(withFlags(memo(FlyoutMessagingComponentWrapper)));
